import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  Grid2,
  IconButton,
  Paper,
} from "@mui/material";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import SkuCopyRow from "../../../../../../views/components/common/SkuCopyRow";
import { InventoryMovementRow } from "../../../../../../models/inventory/InventoryAdmin";
import { formatDate } from "../../../../../../views/components/common/functions/formatDate";

interface AuditSkuDrawerProps {
  open: boolean;
  clientSku: string | null;
  productName?: string | null;
  rows: InventoryMovementRow[];
  isLoading: boolean;
  onClose: () => void;
}

export default function AuditSkuDrawer({
  open,
  clientSku,
  productName,
  rows,
  isLoading,
  onClose,
}: AuditSkuDrawerProps) {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 200,
        "& .MuiDrawer-paper": {
          zIndex: (theme) => theme.zIndex.drawer + 200,
        },
      }}
    >
      <Box
        sx={{
          width: { xs: "100vw", sm: 560, md: 640 },
          height: "100%",
          background:
            "linear-gradient(180deg, rgba(248,249,255,1) 0%, rgba(255,255,255,1) 100%)",
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <CustomTypography variant="heading4" fontWeight={700}>
              SKU Audit Ledger
            </CustomTypography>
            <CustomTypography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {productName || "—"}
            </CustomTypography>
            <Box sx={{ mt: 0.75 }}>
              <SkuCopyRow sku={clientSku} fontWeight={600} />
            </Box>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />

        <Grid2 container spacing={2} sx={{ px: 3, py: 2 }}>
          <Grid2 size={4}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 3 }}>
              <CustomTypography variant="body3" color="text.secondary">
                Entries
              </CustomTypography>
              <CustomTypography variant="heading4" fontWeight={700}>
                {rows.length}
              </CustomTypography>
            </Paper>
          </Grid2>
          <Grid2 size={4}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 3 }}>
              <CustomTypography variant="body3" color="text.secondary">
                Total On-Hand Delta
              </CustomTypography>
              <CustomTypography variant="heading4" fontWeight={700}>
                {rows.reduce((sum, row) => sum + row.onHandDelta, 0)}
              </CustomTypography>
            </Paper>
          </Grid2>
          <Grid2 size={4}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 3 }}>
              <CustomTypography variant="body3" color="text.secondary">
                Total Reserved Delta
              </CustomTypography>
              <CustomTypography variant="heading4" fontWeight={700}>
                {rows.reduce((sum, row) => sum + row.reservedDelta, 0)}
              </CustomTypography>
            </Paper>
          </Grid2>
        </Grid2>

        <Box sx={{ px: 3, pb: 3, overflowY: "auto", height: "calc(100% - 190px)" }}>
          {isLoading ? (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress />
            </Box>
          ) : rows.length === 0 ? (
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, textAlign: "center" }}>
              <CustomTypography variant="body2" color="text.secondary">
                No movement entries found for this SKU.
              </CustomTypography>
            </Paper>
          ) : (
            <Box display="flex" flexDirection="column" gap={2}>
              {rows.map((row) => (
                <Paper key={row.id} elevation={0} sx={{ p: 2, borderRadius: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 2,
                      flexWrap: "wrap",
                    }}
                  >
                    <Box>
                      <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                        <Chip label={row.logType} size="small" />
                        {row.refType && (
                          <Chip
                            label={`${row.refType}${row.refId ? ` #${row.refId}` : ""}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                      <CustomTypography variant="body3" color="text.secondary" sx={{ mt: 1 }}>
                        {formatDate(row.createdAt)}
                      </CustomTypography>
                    </Box>
                    <Box textAlign={{ xs: "left", sm: "right" }}>
                      <CustomTypography
                        variant="body2"
                        fontWeight={700}
                        color={row.onHandDelta < 0 ? "error.main" : "success.main"}
                      >
                        On-hand: {row.onHandDelta > 0 ? "+" : ""}
                        {row.onHandDelta}
                      </CustomTypography>
                      <CustomTypography variant="body3" color="text.secondary">
                        Reserved: {row.reservedDelta > 0 ? "+" : ""}
                        {row.reservedDelta}
                      </CustomTypography>
                    </Box>
                  </Box>
                  <Divider sx={{ my: 1.5 }} />
                  <Grid2 container spacing={1.5}>
                    <Grid2 size={4}>
                      <CustomTypography variant="body3" color="text.secondary">
                        Ledger ID
                      </CustomTypography>
                      <CustomTypography variant="body2" fontWeight={600}>
                        #{row.id}
                      </CustomTypography>
                    </Grid2>
                    <Grid2 size={4}>
                      <CustomTypography variant="body3" color="text.secondary">
                        Pack ID
                      </CustomTypography>
                      <CustomTypography variant="body2" fontWeight={600}>
                        {row.packId ?? "—"}
                      </CustomTypography>
                    </Grid2>
                    <Grid2 size={4}>
                      <CustomTypography variant="body3" color="text.secondary">
                        Admin
                      </CustomTypography>
                      <CustomTypography variant="body2" fontWeight={600}>
                        {row.adminName || row.adminId || "—"}
                      </CustomTypography>
                    </Grid2>
                  </Grid2>
                </Paper>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}
